import { useMemo } from 'react';

import { Accordion, Alert, Badge, Icons } from '@commenda/kakashi';

import { IServiceAuditorReport } from '@commenda/api/agent-platform/service-auditor/types';

import { ServiceAuditReportHelper } from '../helpers/ServiceAuditReportHelper';
import { ServiceAuditReportSection } from './ServiceAuditReportSection';

interface IServiceAuditReportBodyProps {
  report: IServiceAuditorReport;
  // Null on an earlier round, whose saved feedback is read only.
  onFeedbackSaved: (() => Promise<unknown>) | null;
}

export const ServiceAuditReportBody = ({
  report,
  onFeedbackSaved,
}: IServiceAuditReportBodyProps) => {
  const sections = useMemo(
    () => ServiceAuditReportHelper.getReportSections(report),
    [report]
  );
  const defaultOpenSectionKeys = useMemo(
    () => ServiceAuditReportHelper.getDefaultOpenSectionKeys(sections),
    [sections]
  );
  const countBadges = ServiceAuditReportHelper.getCountBadges(report.counts);
  const checksPassedCount =
    ServiceAuditReportHelper.getUniqueChecksPassed(report).length;
  const draftReadCoverageLine =
    ServiceAuditReportHelper.getDraftReadCoverageLine(report.draftReadCoverage);
  const draftReadCoverageWarning =
    ServiceAuditReportHelper.getDraftReadCoverageWarning(
      report.draftReadCoverage
    );

  return (
    <div className="flex flex-col gap-3xl">
      {report.subject?.isFileChangedSinceAudit && (
        <Alert
          variant="warning"
          icon={<Icons.AlertTriangle className="size-5 text-text-warning" />}
          description={ServiceAuditReportHelper.getFileChangedAlert(report)}
        />
      )}
      {draftReadCoverageWarning && (
        <Alert
          variant="warning"
          icon={<Icons.AlertTriangle className="size-5 text-text-warning" />}
          description={draftReadCoverageWarning}
        />
      )}
      <div className="flex flex-col gap-md rounded-xl bg-bg-secondary p-xl">
        <p className="txt-small-medium text-text-primary">
          {ServiceAuditReportHelper.getSummarySentence(
            report.counts,
            checksPassedCount
          )}
        </p>
        {countBadges.length > 0 && (
          <div className="flex flex-wrap items-center gap-md">
            {countBadges.map((badge) => (
              <Badge key={badge.key} size="sm" variant={badge.variant}>
                {badge.label}
              </Badge>
            ))}
          </div>
        )}
        {draftReadCoverageLine && (
          <p className="txt-extra-small-regular text-text-tertiary">
            {draftReadCoverageLine}
          </p>
        )}
        {report.feedbackTotalCount > 0 && (
          <p className="txt-extra-small-regular text-text-tertiary">
            {ServiceAuditReportHelper.getFeedbackProgress(
              report.feedbackGivenCount,
              report.feedbackTotalCount
            )}
          </p>
        )}
      </div>
      {!ServiceAuditReportHelper.hasProvenItems(report.counts) && (
        <div className="flex flex-col items-center gap-md py-6xl text-center">
          <p className="txt-small-medium text-text-primary">
            {ServiceAuditReportHelper.EMPTY_STATE_TITLE}
          </p>
          <p className="txt-small-regular text-text-secondary">
            {ServiceAuditReportHelper.getEmptyStateBody(checksPassedCount)}
          </p>
        </div>
      )}
      {sections.length > 0 && (
        <Accordion
          key={report.runId}
          type="multiple"
          defaultValue={defaultOpenSectionKeys}
          className="flex flex-col gap-3xl"
        >
          {sections.map((section) => (
            <ServiceAuditReportSection
              key={section.config.key}
              section={section}
              onFeedbackSaved={onFeedbackSaved}
            />
          ))}
        </Accordion>
      )}
    </div>
  );
};
